import React from "react";

/**
 * @typedef {Object} IconSpec
 * @property {string} [viewBox="0 0 24 24"] - SVG viewBox.
 * @property {React.ReactNode|string} path - One or more SVG children (e.g., <path/>, <circle/>...) or raw SVG string.
 * @property {string} [fill]  - Fill color. Defaults to `color` prop when omitted.
 * @property {string} [stroke] - Stroke color. Defaults to `color` prop when omitted.
 */

/**
 * @typedef {(props: React.SVGProps<SVGSVGElement>) => JSX.Element} IconComponent
 * A React function component that returns an <svg />.
 */

/**
 * @typedef {IconSpec | IconComponent | React.ReactElement} IconInput
 * Union of accepted icon shapes.
 */

/**
 * Generic Icon
 *
 * Accepts:
 *  - an "icon spec" object ({ viewBox, path, fill?, stroke? }),
 *  - a React component that returns <svg>,
 *  - or a pre-built React <svg> element.
 *
 * Normalizes sizing, color, and basic accessibility across all forms.
 *
 * @param {Object} props
 * @param {IconInput} props.icon - The icon to render (spec, component, or element).
 * @param {string} [props.type=""] - Optional variant tag (not used internally).
 * @param {number} [props.size=24] - Pixel size applied to width/height.
 * @param {string} [props.className=""] - CSS className forwarded to the underlying <svg>.
 * @param {string} [props.color="currentColor"] - Default stroke/fill for spec mode; passed to component mode.
 * @param {number} [props.strokeWidth=2] - Stroke width (spec mode) or forwarded to component mode.
 * @param {string} [props.title] - Accessible label; when provided, icon is announced (role="img").
 * @param {any} [props.rest] - Any other props forwarded to the underlying <svg> (e.g., onClick, data-*, style).
 *
 * @returns {JSX.Element|null} A React element that renders an <svg>, or null when input is invalid.
 */
export function Icon({
  icon,
  size = 24,
  className = "",
  color = "currentColor",
  strokeWidth = 2,
  title,
  ...rest
}) {
  // ─────────────────────────────────────────────────────────────────────────────
  // BRANCH 1: icon is a React component function
  // Input type:   IconComponent
  // Return type:  JSX.Element (an <svg/> produced by the component)
  //
  // icons/Eye.jsx
  // export function Eye(props) {
  //   const { color = "currentColor", strokeWidth = 2, ...rest } = props;
  //   return (
  //     <svg
  //       xmlns="http://www.w3.org/2000/svg"
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke={color}
  //       strokeWidth={strokeWidth}
  //       strokeLinecap="round"
  //       strokeLinejoin="round"
  //       {...rest}
  //     >
  //       <path d="M12 5C5 5 1 12 1 12s4 7 11 7 11-7 11-7-4-7-11-7z" />
  //       <circle cx="12" cy="12" r="3" />
  //     </svg>
  //   );
  // }
  //
  // Anywhere:
  // import { Icon } from "./Icon";
  // import { Eye } from "./icons/Eye";
  //
  // <Icon icon={Eye} size={28} className="text-blue-600" title="Show password" />;
  //
  // ─────────────────────────────────────────────────────────────────────────────
  if (typeof icon === "function") {
    const Comp = /** @type {IconComponent} */ (icon);
    return (
      <Comp
        width={size}
        height={size}
        className={className}
        color={color}
        strokeWidth={strokeWidth}
        aria-hidden={title ? undefined : true}
        role="img"
        {...(title ? { "aria-label": title } : {})}
        {...rest}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // BRANCH 2: icon is an existing React element
  // Input type:   React.ReactElement
  // Return type:  JSX.Element (the cloned <svg/> element)
  // Example:
  // const eyeElement = (
  //   <svg
  //     xmlns="http://www.w3.org/2000/svg"
  //     viewBox="0 0 24 24"
  //     fill="none"
  //     stroke="currentColor"
  //     strokeWidth="2"
  //     strokeLinecap="round"
  //     strokeLinejoin="round"
  //   >
  //     <path d="M12 5C5 5 1 12 1 12s4 7 11 7 11-7 11-7-4-7-11-7z" />
  //     <circle cx="12" cy="12" r="3" />
  //   </svg>
  // );

  // // The Icon will clone it and apply width/height/className/a11y props:
  // <Icon icon={eyeElement} size={20} className="opacity-75" />;

  // ─────────────────────────────────────────────────────────────────────────────
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      width: size,
      height: size,
      className,
      color,
      strokeWidth,
      "aria-hidden": title ? undefined : true,
      role: "img",
      ...(title ? { "aria-label": title } : {}),
      ...rest,
    });
  }

  // if (typeof icon === "object" && icon !== null && !Array.isArray(icon)) {
  //   console.log("this is a data object not JSX");
  //   console.log(icon);
  // }
  // ─────────────────────────────────────────────────────────────────────────────
  // BRANCH 3: icon is treated as an "icon spec" object
  // Input type:   IconSpec | undefined | null
  // Return type:  JSX.Element (an <svg/> we construct) OR null (invalid/missing path)
  // Example:
  // export const eye = {
  //   viewBox: "0 0 24 24",
  //   // Use JSX children:
  //   path: (
  //     <>
  //       <path d="M12 5C5 5 1 12 1 12s4 7 11 7 11-7 11-7-4-7-11-7z" />
  //       <circle cx="12" cy="12" r="3" />
  //     </>
  //   ),
  //   // For outline look:
  //   fill: "none",
  //   // stroke will default to the Icon's `color` prop
  // };
  //
  // import { Icon } from "./Icon";
  // import { eye, eyeOff } from "./icons";
  //
  // <Icon icon={icon} size={22} className="pointer-events-none" />;
  //
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    viewBox = "0 0 24 24",
    path,
    fill = color,
    stroke = color,
  } = /** @type {IconSpec|undefined} */ (icon) || {};

  if (!path) {
    // Return type: null  (no valid content to render)
    return null;
  }

  // NOTE:
  // - `path` may be JSX (ReactNode) or a raw string of SVG children.
  //   React will safely render strings as raw markup for plain text. For raw SVG
  //   tag strings, prefer providing JSX nodes instead of strings to avoid needing
  //   dangerouslySetInnerHTML.
  // - If your icons are "outline" style, set `fill: "none"` in the IconSpec.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={rest.width}
      height={rest.height}
      viewBox={viewBox}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={title ? undefined : true}
      role="img"
      {...(title ? { "aria-label": title } : {})}
      {...rest}
    >
      {path}
    </svg>
  );
}
